import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'

import type { Order } from '@/api/orders'
import { ordersApi } from '@/api/orders'
import { AppShell } from '@/components/AppShell'
import { Alert, BackLink, Button } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { QuantityStepper } from '@/features/list/quantity'
import { PartialSubmit } from '@/features/orders/PartialSubmit'
import { StatusBadge } from '@/features/orders/StatusBadge'
import { CANCELLABLE, formatDate, formatDateTime, formatQuantity } from '@/features/orders/status'
import { toast } from '@/lib/toast'
import { useAuthStore } from '@/stores/authStore'

/** Καταστάσεις στις οποίες ο προμηθευτής μπορεί ακόμη να απαντήσει (§7.6). */
const RESPONDABLE = new Set(['sent', 'received'])
const DELIVERABLE = new Set(['received', 'confirmed', 'partially_confirmed'])

/**
 * Καρτέλα παραγγελίας, και για τις δύο πλευρές.
 *
 * Η επιχείρηση βλέπει τον προμηθευτή και μπορεί να επαναλάβει ή να ακυρώσει.
 * Ο προμηθευτής βλέπει τον πελάτη και απαντά: επιβεβαίωση, διαφορετικές
 * ποσότητες, παράδοση. Ίδιες ενέργειες με τον σύνδεσμο του email, ώστε να
 * μη χρειάζεται να ψάχνει το μήνυμα όταν έχει λογαριασμό.
 */
export default function OrderDetailPage() {
  const { t } = useTranslation()
  const { orderId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const translateError = useApiError()
  const isSupplier = useAuthStore((s) => s.organizationType()) === 'supplier'
  const canCancel = useAuthStore((s) => s.hasPermission('order:cancel')) && !isSupplier
  const canDraft = useAuthStore((s) => s.hasPermission('order:draft')) && !isSupplier
  const canRespond = useAuthStore((s) => s.hasPermission('order:respond')) && isSupplier
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const [reason, setReason] = useState('')
  const [editing, setEditing] = useState(false)
  const [quantities, setQuantities] = useState<Record<string, string>>({})

  const order = useQuery({ queryKey: ['order', orderId], queryFn: () => ordersApi.get(orderId) })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['order', orderId] })
    void queryClient.invalidateQueries({ queryKey: ['orders'] })
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }

  const cancel = useMutation({
    mutationFn: () => ordersApi.cancel(orderId, reason.trim() || undefined),
    onSuccess: () => {
      setConfirmingCancel(false)
      invalidate()
      toast.success(t('orders.cancelledToast'))
    },
  })

  const approveChanges = useMutation({
    mutationFn: () => ordersApi.approveChanges(orderId),
    onSuccess: () => {
      invalidate()
      toast.success(t('orders.changesApprovedToast'))
    },
  })

  // Απόρριψη των αλλαγών του προμηθευτή = ακύρωση με γνωστή αιτία, ώστε ο
  // προμηθευτής να ξέρει γιατί και να μην ετοιμάσει τίποτα.
  const [confirmReject, setConfirmReject] = useState(false)
  const rejectChanges = useMutation({
    mutationFn: () => ordersApi.cancel(orderId, t('orders.rejectReason')),
    onSuccess: () => {
      invalidate()
      toast.success(t('orders.cancelledToast'))
    },
  })

  const duplicate = useMutation({
    mutationFn: () => ordersApi.duplicate(orderId),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['cart'] })
      toast.success(t('orders.duplicatedToast', { count: result.addedItems }))
      navigate('/cart')
    },
  })

  // Πλευρά προμηθευτή: η απάντηση γράφεται απευθείας στην cache, ώστε η
  // κατάσταση να αλλάξει αμέσως και όχι μετά το επόμενο φόρτωμα.
  const answered = (next: Order, message: string) => {
    queryClient.setQueryData(['order', orderId], next)
    setEditing(false)
    invalidate()
    toast.success(message)
  }
  const respondConfirm = useMutation({
    mutationFn: () => ordersApi.respondConfirm(orderId),
    onSuccess: (next) => answered(next, t('orders.respondedToast')),
  })
  const respondPartial = useMutation({
    mutationFn: (note: string) =>
      ordersApi.respondPartial(
        orderId,
        (order.data?.items ?? []).map((item) => ({
          itemId: item.id,
          confirmedQuantity: quantities[item.id] ?? numeric(item.quantity),
        })),
        note,
      ),
    onSuccess: (next) => answered(next, t('orders.respondedToast')),
  })
  const markDelivered = useMutation({
    mutationFn: () => ordersApi.markDelivered(orderId),
    onSuccess: (next) => answered(next, t('orders.deliveredToast')),
  })

  const data = order.data
  // Η τελευταία εξήγηση του προμηθευτή για αλλαγή ποσοτήτων, αν έδωσε.
  const reasonNote = [...(data?.events ?? [])]
    .reverse()
    .find((event) => event.eventType === 'partially_confirmed')?.payload?.note
  const supplierReason = typeof reasonNote === 'string' ? reasonNote : null

  const downloadPdf = useMutation({
    mutationFn: () => ordersApi.downloadPdf(orderId, data?.orderNumber ?? orderId),
  })

  const respondError = respondConfirm.error ?? respondPartial.error ?? markDelivered.error

  return (
    <AppShell title={data?.orderNumber ?? t('common.loading')}>
      <BackLink to="/orders" label={t('common.back')} />

      {order.isPending && <p className="text-gray-500">{t('common.loading')}</p>}
      {order.isError && <Alert>{translateError(order.error)}</Alert>}

      {data && (
        <>
          <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {isSupplier ? (
                  <>
                    <p className="truncate font-semibold text-gray-900">{data.businessName}</p>
                    {(data.businessPhone || data.businessAddress) && (
                      <p className="truncate text-sm text-gray-500">
                        {[data.businessPhone, data.businessAddress].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <Link
                      to={`/suppliers/${data.supplierId}`}
                      className="truncate font-semibold text-gray-900 hover:text-brand-700"
                    >
                      {data.supplierName}
                    </Link>
                    <p className="truncate text-sm text-gray-500">{data.supplierEmail}</p>
                  </>
                )}
              </div>
              <StatusBadge status={data.status} />
            </div>

            <dl className="mt-4 space-y-2 text-sm">
              <Row label={t('orders.sentAt')} value={formatDateTime(data.sentAt)} />
              <Row
                label={t('orders.delivery')}
                value={
                  [
                    data.requestedDeliveryDate ? formatDate(data.requestedDeliveryDate) : null,
                    data.requestedDeliverySlot
                      ? t(`checkout.slots.${data.requestedDeliverySlot}`)
                      : null,
                    data.requestedDeliveryTime?.slice(0, 5) ?? null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'
                }
              />
              {data.receivedAt && (
                <Row label={t('orders.receivedAt')} value={formatDateTime(data.receivedAt)} />
              )}
              {data.cancelReason && (
                <Row label={t('orders.cancelReason')} value={data.cancelReason} />
              )}
            </dl>

            {data.orderNote && (
              <p className="mt-3 rounded-xl bg-gray-50 p-3 text-sm text-gray-700">
                {data.orderNote}
              </p>
            )}
          </section>

          {!isSupplier && data.status === 'partially_confirmed' && (
            <section className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="mb-1 font-semibold text-amber-900">
                {t('orders.quantitiesModified')}
              </p>
              {supplierReason && (
                <p className="mb-3 rounded-xl bg-white/70 p-3 text-sm text-gray-800">
                  <span className="block text-xs font-semibold uppercase tracking-wide text-amber-800">
                    {t('orders.supplierReason')}
                  </span>
                  {supplierReason}
                </p>
              )}
              <ul className="mb-3 flex flex-col gap-1 text-sm text-amber-900">
                {data.items
                  .filter(
                    (item) =>
                      item.confirmedQuantity !== null &&
                      Number(item.confirmedQuantity) !== Number(item.quantity),
                  )
                  .map((item) => (
                    <li key={item.id} className="flex flex-wrap gap-x-2">
                      <span className="font-semibold">{item.name}:</span>
                      <span>
                        {t('orders.requested')} {formatQuantity(item.quantity)}{' '}
                        {t(`units.${item.unit}`)}
                      </span>
                      <span aria-hidden>→</span>
                      <span className="font-semibold">
                        {t('orders.willSend')} {formatQuantity(item.confirmedQuantity!)}{' '}
                        {t(`units.${item.unit}`)}
                      </span>
                    </li>
                  ))}
              </ul>
              <p className="mb-3 text-sm text-amber-800">{t('orders.approveChangesHint')}</p>
              {approveChanges.isError && <Alert>{translateError(approveChanges.error)}</Alert>}
              {rejectChanges.isError && <Alert>{translateError(rejectChanges.error)}</Alert>}
              {canCancel && (
                <div className="flex flex-col gap-2">
                  <Button
                    loading={approveChanges.isPending}
                    onClick={() => approveChanges.mutate()}
                  >
                    {t('orders.approveChanges')}
                  </Button>
                  {confirmReject ? (
                    <div className="flex gap-2">
                      <Button
                        variant="danger"
                        loading={rejectChanges.isPending}
                        onClick={() => rejectChanges.mutate()}
                      >
                        {t('orders.cancelSubmit')}
                      </Button>
                      <Button variant="secondary" onClick={() => setConfirmReject(false)}>
                        {t('common.cancel')}
                      </Button>
                    </div>
                  ) : (
                    <Button variant="secondary" onClick={() => setConfirmReject(true)}>
                      {t('orders.rejectChanges')}
                    </Button>
                  )}
                </div>
              )}
            </section>
          )}

          <section className="mb-4 overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <h2 className="border-b border-gray-100 bg-gray-50 px-4 py-3 text-sm font-medium uppercase tracking-wide text-gray-500">
              {t('orders.items')}
            </h2>
            <ul className="divide-y divide-gray-100">
              {data.items.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-gray-900">{item.name}</p>
                    {item.lineNote && (
                      <p className="truncate text-xs italic text-gray-500">{item.lineNote}</p>
                    )}
                  </div>
                  {editing ? (
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {formatQuantity(item.quantity)} {t(`units.${item.unit}`)} →
                      </span>
                      <QuantityStepper
                        value={quantities[item.id] ?? numeric(item.quantity)}
                        unit={item.unit}
                        compact
                        onChange={(value) =>
                          setQuantities((current) => ({ ...current, [item.id]: value || '0' }))
                        }
                      />
                    </div>
                  ) : (
                    <div className="shrink-0 text-right">
                      <p
                        className={
                          item.confirmedQuantity && item.confirmedQuantity !== item.quantity
                            ? 'text-sm text-gray-400 line-through'
                            : 'font-semibold text-gray-900'
                        }
                      >
                        {formatQuantity(item.quantity)} {t(`units.${item.unit}`)}
                      </p>
                      {item.confirmedQuantity && item.confirmedQuantity !== item.quantity && (
                        <p className="font-semibold text-amber-700">
                          {formatQuantity(item.confirmedQuantity)} {t(`units.${item.unit}`)}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>

          {canRespond && respondError && <div className="mb-4"><Alert>{translateError(respondError)}</Alert></div>}

          {canRespond && RESPONDABLE.has(data.status) && !editing && (
            <div className="mb-4 flex flex-col gap-2">
              <Button loading={respondConfirm.isPending} onClick={() => respondConfirm.mutate()}>
                {t('public.confirmAll')}
              </Button>
              <Button variant="secondary" onClick={() => setEditing(true)}>
                {t('public.declareDifferent')}
              </Button>
            </div>
          )}

          {canRespond && RESPONDABLE.has(data.status) && editing && (
            <div className="mb-4">
              <PartialSubmit
                items={data.items}
                quantities={quantities}
                loading={respondPartial.isPending}
                onSubmit={(note) => respondPartial.mutate(note)}
                onCancel={() => setEditing(false)}
              />
            </div>
          )}

          {canRespond && !RESPONDABLE.has(data.status) && DELIVERABLE.has(data.status) && (
            <div className="mb-4">
              <Button loading={markDelivered.isPending} onClick={() => markDelivered.mutate()}>
                {t('public.markDelivered')}
              </Button>
            </div>
          )}

          <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-500">
              {t('orders.timeline')}
            </h2>
            <ol className="flex flex-col gap-3">
              {data.events.map((event) => (
                <li key={event.id} className="flex gap-3">
                  <span aria-hidden className="mt-1.5 size-2 shrink-0 rounded-full bg-brand-500" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {t(`orders.events.${event.eventType}`, {
                        defaultValue: event.eventType,
                      })}
                    </p>
                    <p className="text-xs text-gray-500">{formatDateTime(event.createdAt)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <div className="flex flex-col gap-2">
            <Button
              variant="secondary"
              loading={downloadPdf.isPending}
              onClick={() => downloadPdf.mutate()}
            >
              {t('orders.openPdf')}
            </Button>

            {canDraft && (
              <Button
                variant="secondary"
                loading={duplicate.isPending}
                onClick={() => duplicate.mutate()}
              >
                {t('orders.duplicate')}
              </Button>
            )}

            {canCancel && CANCELLABLE.includes(data.status) && !confirmingCancel && (
              <Button variant="ghost" onClick={() => setConfirmingCancel(true)}>
                {t('orders.cancel')}
              </Button>
            )}

            {confirmingCancel && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                <p className="mb-3 text-sm font-medium text-red-900">{t('orders.cancelConfirm')}</p>
                {cancel.isError && <Alert>{translateError(cancel.error)}</Alert>}
                <textarea
                  rows={2}
                  maxLength={500}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={t('orders.cancelReasonPlaceholder')}
                  className="mb-3 w-full rounded-xl border border-red-200 bg-white p-3 text-base text-gray-900 outline-none focus:border-red-400"
                />
                <div className="flex gap-2">
                  <Button loading={cancel.isPending} onClick={() => cancel.mutate()}>
                    {t('orders.cancelSubmit')}
                  </Button>
                  <Button variant="secondary" onClick={() => setConfirmingCancel(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </AppShell>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-right font-medium text-gray-900">{value}</dd>
    </div>
  )
}

/** Το API επιστρέφει "5.000"· το χειριστήριο δουλεύει με "5". */
function numeric(value: string): string {
  const number = Number(value)
  return Number.isFinite(number) ? String(number) : value
}
